/**
 * The wrong-patient review (CLA-1693).
 *
 * When a consult turns out not to be a patient's (a note's "Change patient",
 * or "Not this person" on a Timely booking), the note moves, but what it
 * already wrote onto the first patient's record stays. The backend never
 * removes it by itself: it opens one task ("Check Cate's record") whose items
 * the clinician keeps or removes, one by one.
 *
 * Backend: claro-elixir `Claro.Clinical.WrongPatientReview`.
 *
 *  * A task that is a review carries `record_review: {items, open}`.
 *  * `GET  /tasks/:ref/review` — the items, each with what can be done.
 *  * `POST /tasks/:ref/review/items/:uuid {decision: keep|remove}` — remove
 *    goes through the record's own path (a drug-register line is voided,
 *    never deleted). A record with no way back is `removable: false`: only
 *    keep is accepted (422 `not_removable` otherwise).
 *  * The change itself answers with `record_review: {task_ref, items}` (the
 *    note's `POST /notes/:ref/patient`, the booking's `DELETE …/patient`).
 *
 * ⚠ PRIVACY. Items name another person's clinical details. Query keys and
 * URLs carry only the task ref; a decision opts out of the global error toast
 * (the panel says what went wrong in its own words).
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, ApiError } from './client'
import type { components } from './schema'

export type RecordReview = components['schemas']['RecordReviewResponse']
export type RecordReviewItem = components['schemas']['RecordReviewItem']
export type RecordReviewSummary = components['schemas']['RecordReviewSummary']
export type RecordReviewDecision = 'keep' | 'remove'

export const recordReviewKeys = {
  review: (taskRef: string) => ['record-review', taskRef] as const,
}

export function useRecordReview(taskRef: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: recordReviewKeys.review(taskRef),
    queryFn: () => api.get<RecordReview>(`/tasks/${taskRef}/review`),
    enabled,
  })
}

/**
 * What a removal touched lives under the patient's own queries (allergies,
 * medications, plan, timeline …), all keyed by the patient's uuid.
 */
function refreshAfterDecision(qc: QueryClient, taskRef: string, patientRef: string | null | undefined) {
  qc.invalidateQueries({ queryKey: recordReviewKeys.review(taskRef) })
  qc.invalidateQueries({ queryKey: ['tasks'] })
  if (patientRef) {
    qc.invalidateQueries({ predicate: (q) => q.queryKey.includes(patientRef) })
  }
}

export function useDecideRecordReviewItem(taskRef: string, patientRef?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ itemUuid, decision }: { itemUuid: string; decision: RecordReviewDecision }) =>
      api.post<components['schemas']['RecordReviewDecisionResponse']>(
        `/tasks/${taskRef}/review/items/${itemUuid}`,
        { decision },
      ),
    meta: { errorToast: false },
    onSettled: () => refreshAfterDecision(qc, taskRef, patientRef),
  })
}

/** The server's sentence for a refused decision, or a plain fallback. */
export function decisionProblem(err: unknown): string {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as { detail?: unknown }
    if (typeof body.detail === 'string' && body.detail.trim()) return body.detail
    if (err.status === 403) return "You can't change this patient's record."
  }
  return "That didn't save. Try again."
}

/**
 * The review a change opened, as one sentence for a toast, or null. The
 * change's response carries `record_review` only when there was something
 * to review.
 */
export function reviewOpenedMessage(summary: RecordReviewSummary | null | undefined): string | null {
  if (!summary || summary.items < 1) return null
  return summary.items === 1
    ? 'One thing from this consult is still on the first patient’s record. Check it in Tasks.'
    : `${summary.items} things from this consult are still on the first patient’s record. Check them in Tasks.`
}
